import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getShakedown } from '../lib/api';
import { ShakedownResults, ShareButtons } from '../components';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatWeight(grams: number): string {
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(1)}kg`;
  }
  return `${grams}g`;
}

export function SavedShakedown() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['shakedown', slug],
    queryFn: () => getShakedown(slug!),
    enabled: !!slug,
  });

  // Update document title
  useEffect(() => {
    if (data) {
      document.title = `Gear Shakedown | NFA`;
    }
    return () => {
      document.title = 'NFA - Gear Recommendations';
    };
  }, [data]);

  const handleAnalyzeAnother = () => {
    navigate('/shakedown');
  };

  // Build share description
  const shareDescription = data
    ? `Total weight: ${formatWeight(data.result.totals.total_weight_g)} | ${data.result.totals.item_count} items | ${data.result.optimizations.length} optimizations`
    : '';

  if (isLoading) {
    return (
      <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" />
          </div>
        </div>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-3xl mx-auto">
          <nav className="mb-8">
            <Link
              to="/shakedown"
              className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Shakedown
            </Link>
          </nav>

          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-8 text-center">
            <h2 className="font-mono text-xl font-bold text-red-400 mb-2">Shakedown Not Found</h2>
            <p className="text-neutral-400 mb-6">
              This shakedown doesn't exist or may have been removed.
            </p>
            <Link
              to="/shakedown"
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-black font-bold rounded-lg hover:bg-orange-400 transition-colors"
            >
              <span>Analyze your gear</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <nav className="mb-8">
          <Link
            to="/shakedown"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Shakedown
          </Link>
        </nav>

        {/* Meta info */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-mono text-2xl sm:text-3xl font-bold text-white mb-2">
              Gear Shakedown
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-neutral-500">
              <span>Created {formatDate(data.created_at)}</span>
              <span>·</span>
              <span>{data.view_count} views</span>
            </div>
          </div>
          <ShareButtons
            url={window.location.href}
            title="Gear Shakedown"
            description={shareDescription}
            variant="compact"
          />
        </div>

        {/* Results */}
        <ShakedownResults
          result={data.result}
          onSave={() => {}}
          onReset={handleAnalyzeAnother}
          isSaving={false}
          savedUrl={window.location.href}
        />

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-6 sm:p-8">
          <h2 className="font-mono text-xl sm:text-2xl font-bold text-white mb-3">
            Want to analyze your own gear?
          </h2>
          <p className="text-neutral-400 mb-6">
            Paste your gear list and get personalized optimization suggestions.
          </p>
          <Link
            to="/shakedown"
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-black font-bold rounded-lg hover:bg-orange-400 transition-colors"
          >
            <span>Shakedown your gear</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </div>
    </main>
  );
}
